import React, { useEffect, useMemo, useState } from "react";
import { MapPin, Calendar, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getStoredData, saveToGlobalState } from "../../utils/localStorage";
import StepFooter from "../../components/step-footer/StepFooter";
import "./DataHora.css";

const addDays = (value, days) => {
  const date = parseValueToLocalDate(value);
  if (!date) return null;
  date.setDate(date.getDate() + days);
  return dateToValue(date);
};

const MONTHS_PT = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const WEEK_DAYS_PT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

const pad2 = (n) => String(n).padStart(2, "0");

const dateToValue = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const parseValueToLocalDate = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const compareDateValues = (a, b) => a.localeCompare(b);

const isSameDate = (a, b) => a && b && a === b;

const isBeforeDateValue = (a, b) => compareDateValues(a, b) < 0;

const getTodayValue = () => dateToValue(new Date());

const formatDisplayDate = (value) => {
  const date = parseValueToLocalDate(value);
  if (!date) return "";
  return `${date.getDate()} ${MONTHS_PT[date.getMonth()]}, ${date.getFullYear()}`;
};

const formatSearchDate = (value) => {
  const date = parseValueToLocalDate(value);
  if (!date) return "";
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;
};

const generateTimes = (stepMinutes = 15) => {
  const times = [];
  for (let h = 0; h < 24; h += 1) {
    for (let m = 0; m < 60; m += stepMinutes) {
      times.push(`${pad2(h)}:${pad2(m)}`);
    }
  }
  return times;
};

const timeToMinutes = (time) => {
  if (!time) return 0;
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

const minutesToTime = (minutes) => {
  const normalized = ((minutes % 1440) + 1440) % 1440;
  const h = Math.floor(normalized / 60);
  const m = normalized % 60;
  return `${pad2(h)}:${pad2(m)}`;
};

const addHoursToTime = (time, hours) =>
  minutesToTime(timeToMinutes(time) + hours * 60);

const getNextAvailableTime = (stepMinutes = 15) => {
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const nextSlot = Math.ceil(currentMinutes / stepMinutes) * stepMinutes;
  return minutesToTime(nextSlot);
};

function CalendarPicker({
  title,
  selectedValue,
  minValue,
  maxValue,
  onSelect,
}) {
  const initialMonth = useMemo(() => {
    const base = selectedValue || minValue || getTodayValue();
    const date = parseValueToLocalDate(base);
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }, [selectedValue, minValue]);

  const [viewMonth, setViewMonth] = useState(initialMonth);

  useEffect(() => {
    const base = selectedValue || minValue || getTodayValue();
    const date = parseValueToLocalDate(base);
    setViewMonth(new Date(date.getFullYear(), date.getMonth(), 1));
  }, [selectedValue, minValue]);

  const minDateObj = parseValueToLocalDate(minValue);
  const minMonthObj = minDateObj
    ? new Date(minDateObj.getFullYear(), minDateObj.getMonth(), 1)
    : null;

  const prevMonth = new Date(
    viewMonth.getFullYear(),
    viewMonth.getMonth() - 1,
    1,
  );
  const nextMonth = new Date(
    viewMonth.getFullYear(),
    viewMonth.getMonth() + 1,
    1,
  );

  const canGoPrev = !minMonthObj
    ? true
    : viewMonth.getFullYear() > minMonthObj.getFullYear() ||
      (viewMonth.getFullYear() === minMonthObj.getFullYear() &&
        viewMonth.getMonth() > minMonthObj.getMonth());

  const daysInMonth = new Date(
    viewMonth.getFullYear(),
    viewMonth.getMonth() + 1,
    0,
  ).getDate();

  const firstDayOfMonth = new Date(
    viewMonth.getFullYear(),
    viewMonth.getMonth(),
    1,
  );
  const startOffset = (firstDayOfMonth.getDay() + 6) % 7;

  const cells = [];

  for (let i = 0; i < startOffset; i += 1) cells.push(null);

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), day));
  }

  while (cells.length % 7 !== 0) cells.push(null);

  const isDisabled = (dateObj) => {
    if (!dateObj) return true;

    const value = dateToValue(dateObj);

    if (minDateObj && isBeforeDateValue(value, dateToValue(minDateObj))) {
      return true;
    }

    if (maxValue) {
      const maxDateObj = parseValueToLocalDate(maxValue);
      if (maxDateObj && isBeforeDateValue(dateToValue(maxDateObj), value)) {
        return true;
      }
    }

    return false;
  };

  return (
    <>
      <div className="calendar-card">
        <div className="calendar-header">
          <div>
            <p className="calendar-title">{title}</p>
            <p className="calendar-month">
              {MONTHS_PT[viewMonth.getMonth()]} {viewMonth.getFullYear()}
            </p>
          </div>

          <div className="calendar-nav">
            <button
              type="button"
              className="calendar-nav-btn"
              onClick={() => canGoPrev && setViewMonth(prevMonth)}
              disabled={!canGoPrev}
              aria-label="Mês anterior"
            >
              ‹
            </button>
            <button
              type="button"
              className="calendar-nav-btn"
              onClick={() => setViewMonth(nextMonth)}
              aria-label="Próximo mês"
            >
              ›
            </button>
          </div>
        </div>

        <div className="weekday-row">
          {WEEK_DAYS_PT.map((day) => (
            <div key={day} className="weekday-cell">
              {day}
            </div>
          ))}
        </div>

        <div className="days-grid">
          {cells.map((dateObj, index) => {
            if (!dateObj) {
              return <div key={`empty-${index}`} className="day-cell empty" />;
            }

            const value = dateToValue(dateObj);
            const selected = selectedValue === value;
            const disabled = isDisabled(dateObj);

            return (
              <button
                key={value}
                type="button"
                className={`day-cell ${selected ? "selected" : ""} ${disabled ? "disabled" : ""}`}
                onClick={() => !disabled && onSelect(value)}
                disabled={disabled}
              >
                {dateObj.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

function TimeSelect({
  label,
  value,
  onChange,
  options,
  disabled,
  helperText,
  className,
}) {
  return (
    <div className="field-block">
      <label className="field-label">{label}</label>
      <select
        className={`time-select ${className || ""}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        <option value="">Selecionar hora</option>
        {options.map((time) => (
          <option key={time} value={time}>
            {time}
          </option>
        ))}
      </select>
      {helperText ? <p className="helper-text">{helperText}</p> : null}
    </div>
  );
}

export default function DataHora() {
  const navigate = useNavigate();
  const stored = getStoredData();
  const today = getTodayValue();

  const [step, setStep] = useState(stored?.viagem_step ?? 0);

  const [origemCidade, setOrigemCidade] = useState(
    stored.cidade_origem_main || "",
  );
  const [destinoCidade, setDestinoCidade] = useState(
    stored.cidade_destino_main || "",
  );
  const [origemData, setOrigemData] = useState(stored.origemData || today);
  const [origemHora, setOrigemHora] = useState(stored.origemHora || "");
  const [destinoData, setDestinoData] = useState(
    stored.destinoData || stored.origemData || today,
  );
  const [destinoHora, setDestinoHora] = useState(stored.destinoHora || "");
  const [blinkOrigem, setBlinkOrigem] = useState(false);
  const [blinkDestino, setBlinkDestino] = useState(false);

  const allTimes = useMemo(() => generateTimes(15), []);
  const maxDestinoDate = origemData ? addDays(origemData, 6) : null;

  useEffect(() => {
    saveToGlobalState({
      viagem_step: step,
    });
  }, [step]);

  const handleBackTransport = () => {
    const stored = getStoredData();
    const type = stored?.qual_meio_transporte;

    const routes = {
      aviao: "/aviao",
      onibus: "/onibus",
      trem: "/trem",
      carro: "/trajeto",
      moto: "/trajeto",
    };

    const route = routes[type];

    if (route) navigate(route);
  };

  const origemTimes = useMemo(() => {
    if (!origemData) return [];

    if (isSameDate(origemData, today)) {
      const minNow = getNextAvailableTime(15);
      return allTimes.filter(
        (time) => timeToMinutes(time) >= timeToMinutes(minNow),
      );
    }

    return allTimes;
  }, [allTimes, origemData, today]);

  const destinoMinDate = origemData || today;

  const destinoTimes = useMemo(() => {
    if (!destinoData || !origemHora) return [];

    if (isSameDate(destinoData, origemData)) {
      const minDestination = addHoursToTime(origemHora, 2);
      return allTimes.filter(
        (time) => timeToMinutes(time) >= timeToMinutes(minDestination),
      );
    }

    return allTimes;
  }, [allTimes, destinoData, origemData, origemHora]);

  const origemCompleta = Boolean(origemData && origemHora);
  const destinoCompleto = Boolean(destinoData && destinoHora);

  useEffect(() => {
    saveToGlobalState({
      dia_mes_ano_ida_viagem: formatDisplayDate(origemData),
      dia_mes_ano_ida_viagem_para_busca: formatSearchDate(origemData),
      horas_ida_viagem: origemHora,

      dia_mes_ano_ida_destino: formatDisplayDate(destinoData),
      dia_mes_ano_ida_destino_para_busca: formatSearchDate(destinoData),
      horas_ida_destino: destinoHora,

      origemData,
      origemHora,
      destinoData,
      destinoHora,
    });
  }, [origemData, origemHora, destinoData, destinoHora]);

  useEffect(() => {
    if (destinoData && isBeforeDateValue(destinoData, origemData || today)) {
      setDestinoData("");
      setDestinoHora("");
    }
  }, [origemData, destinoData, today]);

  useEffect(() => {
    if (
      origemData &&
      destinoData &&
      origemHora &&
      destinoHora &&
      isSameDate(origemData, destinoData)
    ) {
      const minDestination = addHoursToTime(origemHora, 2);
      if (timeToMinutes(destinoHora) < timeToMinutes(minDestination)) {
        setDestinoHora("");
      }
    }
  }, [origemData, origemHora, destinoData, destinoHora]);

  useEffect(() => {
    if (step === 1 && !origemCompleta) {
      setStep(0);
    }
  }, [step, origemCompleta]);

  const goToDestination = () => {
    if (origemCompleta) setStep(1);
  };

  const goBackToOrigin = () => setStep(0);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== "Enter") return;

      // evita comportamento padrão
      e.preventDefault();

      // STEP 0 → origem
      if (step === 0) {
        if (!origemCompleta) {
          setBlinkOrigem(true);
          setTimeout(() => setBlinkOrigem(false), 1500);
          return;
        }

        goToDestination();
        return;
      }

      // STEP 1 → destino
      if (step === 1) {
        if (!destinoCompleto) {
          setBlinkDestino(true);
          setTimeout(() => setBlinkDestino(false), 1500);
          return;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    step,
    origemCompleta,
    destinoCompleto,
    origemData,
    origemHora,
    destinoData,
    destinoHora,
  ]);

  return (
    <>
      <div className="page-layout">
        <div className="viagem-shell">
          <div className="viagem-container">
            <div className="top-layout">
              <div className="left-side">
                <div className="hero-card">
                  <div className="hero-copy">
                    {/*<span className="hero-kicker">Planeia a tua viagem</span>*/}
                    <h2 className="page-title">
                      {step === 0
                        ? "Defina a data e hora da sua ida"
                        : "Defina a data e hora do seu destino"}
                    </h2>
                    <p className="page-subtitle">
                      Seleciona a data e a hora de saída e chegada.
                    </p>
                  </div>

                  <div className="stepper" aria-label="Progresso da seleção">
                    <div className={`step-dot ${step === 0 ? "active" : ""}`}>
                      1
                    </div>
                    <div
                      className={`step-line ${step === 1 ? "filled" : ""}`}
                    />
                    <div className={`step-dot ${step === 1 ? "active" : ""}`}>
                      2
                    </div>
                  </div>
                </div>

                <div className="summary-row">
                  <div className="summary-card">
                    <div className="summary-top">
                      <span className="summary-label">
                        <MapPin size={14} className="summary-icon-label" />
                        {step === 0 ? "Saída" : "Destino"}
                      </span>
                    </div>

                    <div className="summary-bottom">
                      <strong className="summary-city">
                        {step === 0 ? origemCidade : destinoCidade}
                      </strong>

                      <div className="summary-info">
                        <span>
                          <Calendar size={16} className="summary-icon-info" />
                          {step === 0
                            ? formatDisplayDate(origemData)
                            : formatDisplayDate(destinoData)}
                        </span>

                        <span>
                          <Clock size={16} className="summary-icon-info" />
                          {step === 0
                            ? origemHora || "--:--"
                            : destinoHora || "--:--"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="time-wrapper desktop-time">
                  {step === 0 ? (
                    <TimeSelect
                      label="Hora de Ida"
                      value={origemHora}
                      onChange={(value) => {
                        setOrigemHora(value);
                        if (
                          destinoData &&
                          isSameDate(origemData, destinoData)
                        ) {
                          const minDestination = addHoursToTime(value, 2);
                          if (
                            destinoHora &&
                            timeToMinutes(destinoHora) <
                              timeToMinutes(minDestination)
                          ) {
                            setDestinoHora("");
                          }
                        }
                      }}
                      options={origemTimes}
                      disabled={!origemData}
                      helperText={
                        origemData && origemTimes.length === 0
                          ? "Não há horas disponíveis para esta data."
                          : ""
                      }
                      className={blinkOrigem ? "blink-shadow" : ""}
                    />
                  ) : (
                    <TimeSelect
                      label="Hora de Destino"
                      value={destinoHora}
                      onChange={(value) => setDestinoHora(value)}
                      options={destinoTimes}
                      disabled={!destinoData || !origemHora}
                      helperText={
                        destinoData &&
                        origemData &&
                        isSameDate(destinoData, origemData) &&
                        origemHora &&
                        destinoTimes.length === 0
                          ? "No mesmo dia, o destino precisa ficar pelo menos 2 horas depois da origem."
                          : ""
                      }
                      className={blinkDestino ? "blink-shadow" : ""}
                    />
                  )}
                </div>
              </div>
              <div className="right-side">
                <div className="slider-viewport">
                  <div
                    className="slider-track"
                    style={{ transform: `translateX(-${step * 100}%)` }}
                  >
                    <section className="panel">
                      <div className="section section-highlight">
                        <h3 className="section-title">Dia da ida</h3>

                        <CalendarPicker
                          title="Data de Ida"
                          selectedValue={origemData}
                          minValue={today}
                          onSelect={(value) => {
                            setOrigemData(value);
                            setOrigemHora("");
                            setDestinoData(value);
                            setDestinoHora("");
                          }}
                        />
                      </div>
                    </section>

                    <section className="panel">
                      <div className="section section-highlight alt">
                        <h3 className="section-title">Dia da Chegada</h3>

                        <CalendarPicker
                          title="Data de Destino"
                          selectedValue={destinoData}
                          minValue={destinoMinDate}
                          maxValue={maxDestinoDate}
                          onSelect={(value) => {
                            setDestinoData(value);
                            setDestinoHora("");
                          }}
                        />
                      </div>
                    </section>
                  </div>
                </div>
              </div>
              <div className="time-wrapper mobile-time">
                {step === 0 ? (
                  <TimeSelect
                    label="Hora de Ida"
                    value={origemHora}
                    onChange={(value) => {
                      setOrigemHora(value);
                      if (destinoData && isSameDate(origemData, destinoData)) {
                        const minDestination = addHoursToTime(value, 2);
                        if (
                          destinoHora &&
                          timeToMinutes(destinoHora) <
                            timeToMinutes(minDestination)
                        ) {
                          setDestinoHora("");
                        }
                      }
                    }}
                    options={origemTimes}
                    disabled={!origemData}
                    helperText={
                      origemData && origemTimes.length === 0
                        ? "Não há horas disponíveis para esta data."
                        : ""
                    }
                    className={blinkOrigem ? "blink-shadow" : ""}
                  />
                ) : (
                  <TimeSelect
                    label="Hora de Destino"
                    value={destinoHora}
                    onChange={(value) => setDestinoHora(value)}
                    options={destinoTimes}
                    disabled={!destinoData || !origemHora}
                    helperText={
                      destinoData &&
                      origemData &&
                      isSameDate(destinoData, origemData) &&
                      origemHora &&
                      destinoTimes.length === 0
                        ? "No mesmo dia, o destino precisa ficar pelo menos 2 horas depois da origem."
                        : ""
                    }
                    className={blinkDestino ? "blink-shadow" : ""}
                  />
                )}
              </div>
            </div>
            <div className="actions split actions-row">
              {step === 0 ? (
                <>
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={handleBackTransport}
                  >
                    VOLTAR
                  </button>

                  <button
                    type="button"
                    className="primary-btn"
                    onClick={() => {
                      if (!origemCompleta) {
                        setBlinkOrigem(true);

                        setTimeout(() => {
                          setBlinkOrigem(false);
                        }, 1500);

                        return;
                      }

                      goToDestination();
                    }}
                    //disabled={!origemCompleta}
                  >
                    PROXIMO
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={goBackToOrigin}
                  >
                    VOLTAR
                  </button>

                  <button
                    type="button"
                    className="primary-btn"
                    //disabled={!destinoCompleto}
                    onClick={() => {
                      if (!destinoCompleto) {
                        setBlinkDestino(true);

                        setTimeout(() => {
                          setBlinkDestino(false);
                        }, 1500);

                        return;
                      }

                      const stored = getStoredData();

                      const transporte = stored?.qual_meio_transporte;
                      const editarTrajeto = stored?.editar_trajeto;

                      if (editarTrajeto === "sim") {
                        navigate("/espaco-disponivel");
                        return;
                      }

                      if (
                        editarTrajeto == null &&
                        (transporte === "onibus" || transporte === "aviao")
                      ) {
                        navigate("/espaco-disponivel");
                        return;
                      }

                      if (
                        (transporte === "moto" || transporte === "carro") &&
                        editarTrajeto !== "sim"
                      ) {
                        navigate("/cidades-trajeto");
                        return;
                      }
                    }}
                  >
                    PROXIMO
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        <StepFooter />
      </div>
    </>
  );
}
